// Port of android/.../viewmodels/PinnedPostsPresentation.kt (Apple has no twin: its
// PinnedMessagesSheet derives the rows inline — the Kotlin object is the one with tests).
//
// The channel's pinned messages as the board draws them (shared/API.md §21) — pure, so the
// ordering and every label it shows are testable without a database or a component.
//
// The rows are deliberately NOT message rows. A pinned board is a list of references: who said
// it, when, and enough of it to recognise, with the message itself one tap away in the
// conversation it belongs to (shared/API.md §25). Rendering the full message here would reproduce
// attachments, reactions, replies and the actions menu inside a panel, which is the mistake the
// thread pane exists to avoid making twice.
import { messagePreview } from './channelListDerivation';
import { RelativeTime } from './relativeTime';
import * as TeammateNameDisplay from './teammateNameDisplay';
import type { PostRecord, UserRecord } from './records';

/**
 * `postId` is where tapping the row goes: the channel, scrolled to this exact message. A pinned
 * REPLY jumps to the reply itself, not to the thread it lives in — the notice is the line
 * somebody pinned, not the conversation it happened to be part of.
 */
export interface Row {
  postId: string;
  userId: string;
  authorName: string;
  timeLabel: string;
  preview: string;
}

/**
 * @param posts already newest-first from `PostsDAO.pinnedPosts` — the order is the DAO's answer
 *   and is not re-sorted here, so the board and the query can never disagree about "newest".
 * @param attachmentPlaceholder what a message with no text reads as; a pinned attachment is a
 *   perfectly ordinary thing to pin and a blank row looks like a rendering failure.
 * @param unknownAuthorLabel for an author this device has never synced — the same honest
 *   stand-in the who-reacted sheet uses, rather than a bare id.
 */
export function rows(args: {
  posts: readonly PostRecord[];
  usersById: ReadonlyMap<string, UserRecord>;
  nameFormat: TeammateNameDisplay.Format;
  now: number;
  yesterdayLabel: string;
  attachmentPlaceholder: string;
  unknownAuthorLabel: string;
  timeZone?: string;
  locale?: string;
}): Row[] {
  return args.posts.map((post) => {
    const author = args.usersById.get(post.userId);
    return {
      postId: post.id,
      userId: post.userId,
      authorName: author ? TeammateNameDisplay.name(author, args.nameFormat) : args.unknownAuthorLabel,
      timeLabel: RelativeTime.format(post.createAt, args.now, { yesterday: args.yesterdayLabel }, args.timeZone, args.locale),
      // The same flattening the channel list's second line does, and for the same reasons:
      // Markdown that reads as its source is noise, and a pasted stack trace must not decide how
      // tall this row is.
      preview: messagePreview({
        message: post.message,
        authorName: null,
        isOwnMessage: false,
        showsAuthorPrefix: false,
        youLabel: '',
        attachmentPlaceholder: args.attachmentPlaceholder,
      }),
    };
  });
}

/** Namespace form, for `PinnedPostsPresentation.rows(...)` call sites. */
export const PinnedPostsPresentation = { rows } as const;
